import React, {useCallback, useEffect, useState} from "react"
import { connect } from "react-redux"
import classNames from "classnames"
import Select, { components as SelectComponents } from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faSortAmountDownAlt, faSortAmountUpAlt } from "@fortawesome/free-solid-svg-icons"
import { Input, Tooltip } from "@material-ui/core";
import {
  searchDispatchAction,
  updateSelectedProductsDispatchAction
} from "../../../state/product/dispatchActions"
import {toggleFramingViewAction, toggleProductsViewAction} from "../../../state/product/actions"
import {
  getDefaultOrderByParams, getScopeConfigurationFieldsBy,
  getSearchFormValuesByState,
} from "../../../helpers/configurationHelper";
import { areAllSearchResultsCheckboxesSelected } from "../../../helpers/productHelper";
import InputCheckbox from "../../shared/inputs/InputCheckbox";
import InputText from "../../shared/inputs/InputText";
import { ConfigurationsType } from "../../../types/configurations";
import { ProductImageStacType, SearchResultsType } from "../../../types/product";
import {
  getAllSelectableProductImagesFrom,
  getOrderByFieldNameOptionsFrom
} from "../../../helpers/searchResultsHelper";
import {
  isStackEnabledOnMission
} from "../../../helpers/interferometricStackHelper";
import { FIRST_SEARCH_PAGE_NUMBER } from "../../../helpers/searchHelper";
import { ShapeType } from "../../../types/shape";
import { updateQuicklookPreviewsAction } from "../../../state/geoMap/actions";
import {
  getAllQuicklookPreviewsFromProductImages, toggleQuicklookPreviewFrom
} from "../../../helpers/quicklookPreviewHelper";
import { QuicklookPreviewType } from "../../../types/geoMap";
import { AllQuicklookPreviewsType } from "../../../state/geoMap/types";
import { DEFAULT_TOOLTIP_PROPS } from "../../../helpers/tooltipHelper";
import { CartOrderType } from "../../../types/cart";
import translations from "../../../translations";
import {updateFormDataPayloadAction} from "../../../state/application/actions";
import {AppParametersType} from "../../../types/app";

interface PropsType {
  configurations: ConfigurationsType,
  ordersSubmitted: CartOrderType[],
  formData: any,
  isListViewActive: boolean,
  isFramingViewActive: boolean,
  selectedProducts: ProductImageStacType[],
  searchResults: SearchResultsType,
  searchFormValues: any,
  updateSelectedProductsActionHandler: (
    configurations: ConfigurationsType,
    searchFormValues: any,
    selectedArea: ShapeType,
    isInterferometricStackSearch: boolean,
    items: ProductImageStacType[],
    isChecked: boolean,
    language: string
  ) => {},
  toggleProductsViewActionHandler: any,
  toggleFramingViewActionHandler: any,
  updateQuicklookPreviewsActionHandler: any,
  searchDispatchActionHandler: any,
  isInterferometricStackSearch: boolean,
  selectedArea: ShapeType,
  allQuicklookPreviews: AllQuicklookPreviewsType,
  language: string,
  searchByProducts: boolean,
  searchProductIds: any,
  updateFormDataPayloadActionHandler: any,
  appParameters: AppParametersType
}

const HeaderSearchResultsSidebar = ({
  configurations,
  ordersSubmitted,
  formData,
  searchResults,
  isListViewActive,
  isFramingViewActive,
  selectedProducts,
  searchFormValues,
  searchDispatchActionHandler,
  updateSelectedProductsActionHandler,
  toggleProductsViewActionHandler,
  toggleFramingViewActionHandler,
  updateQuicklookPreviewsActionHandler,
  isInterferometricStackSearch,
  selectedArea,
  allQuicklookPreviews,
  language,
  searchByProducts,
  searchProductIds,
  updateFormDataPayloadActionHandler,
  appParameters
}: PropsType) => {
  const [isMaxBaselineInvalid, setIsMaxBaselineInvalid] = useState(false);

  const [maxBaselineInputValue, setMaxBaselineInputValue] = useState(`${formData.maxBaseline || ""}`);

  const search = useCallback((
    type: 'ORDER_BY_SORT_TYPE'
      | 'ORDER_BY_FIELD_NAME'
      | 'STACK_HEADER_SEARCH'
      | 'IMAGES_HEADER_SEARCH',
    newFormData?: any,
  ) => {
    switch (type) {
      case "ORDER_BY_SORT_TYPE":
      case "ORDER_BY_FIELD_NAME": {
        if (!newFormData) { return; }
        searchDispatchActionHandler(
          { ...newFormData, token: null },
          false,
          isInterferometricStackSearch,
          language,
          searchByProducts,
          searchProductIds
        );
        return;
      }
      case "STACK_HEADER_SEARCH": {
        if (!isInterferometricStackSearch) {
          searchDispatchActionHandler({
            ...formData,
            token: null,
            orderBy: getDefaultOrderByParams(configurations, searchFormValues, true, language),
            language,
            searchByProducts,
            searchProductIds
          }, true, true);
        }

        return;
      }
      case "IMAGES_HEADER_SEARCH": {
        if (isInterferometricStackSearch) {
          searchDispatchActionHandler({
            ...formData,
            token: null,
            orderBy: getDefaultOrderByParams(configurations, searchFormValues, false, language),
            language,
            searchByProducts,
            searchProductIds
          }, true, false)
        }

        break;
      }
      default: {
        break;
      }
    }
  }, [
    configurations,
    formData,
    isInterferometricStackSearch,
    language,
    searchByProducts,
    searchDispatchActionHandler,
    searchFormValues,
    searchProductIds
  ]);

  const onChangeBaselineInput = (e:React.ChangeEvent<HTMLInputElement>): void => {
    const stringedMaxBaseline = e?.target?.value;

    const maxBaseline = Number(stringedMaxBaseline);
    const invalidMaxBaseline = Number.isNaN(maxBaseline);

    if (invalidMaxBaseline || stringedMaxBaseline === undefined) return;
    if (stringedMaxBaseline[0] === "0") return;

    setMaxBaselineInputValue(stringedMaxBaseline);
  }

  useEffect(() => {
    const newValue = Number(maxBaselineInputValue);
    const maxBaseline = newValue || undefined;

    const updateStore = setTimeout(() => {
      updateFormDataPayloadActionHandler({
        maxBaseline
      });
    }, 300);

    return () => clearTimeout(updateStore);
  }, [maxBaselineInputValue, updateFormDataPayloadActionHandler]);

  const orderByFieldNameOptions = getOrderByFieldNameOptionsFrom(configurations, searchResults, language);
  const orderByFieldTypeOptions = [
    { label: "ASC", value: "ASC" },
    { label: "DESC", value: "DESC" }
  ];

  const FieldTypeSelectComponents = {
    SingleValue: (singleValueProps: any) => (
      <div className="input-select">
        <div className="input-select__single-value">
          <span className="input-select__icon">
            {
              singleValueProps.data.value === "ASC"
                ? <FontAwesomeIcon className="fas fa-sort-amount-down-alt" icon={faSortAmountDownAlt} />
                : <FontAwesomeIcon className="fas fa-sort-amount-up-alt" icon={faSortAmountUpAlt} />
            }
          </span>
        </div>
      </div>
    ),
    Option: (optionProps: any) => (
      <SelectComponents.Option className="p-1" {...optionProps}>
        {
          optionProps.data.value === "ASC"
            ? <FontAwesomeIcon className="fas fa-sort-amount-down-alt" icon={faSortAmountDownAlt} />
            : <FontAwesomeIcon className="fas fa-sort-amount-up-alt" icon={faSortAmountUpAlt} />
        }
      </SelectComponents.Option>
    )
  };

  const isSelectAllChecked = areAllSearchResultsCheckboxesSelected(configurations, ordersSubmitted, searchResults, selectedProducts);
  const allSelectableProductImages = getAllSelectableProductImagesFrom(configurations, ordersSubmitted, searchResults);

  const onChangeSelectAll = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;

    const checkIsMaster = (item: ProductImageStacType): boolean => item?.isMasterItem || formData.masterId === item.id;

    const withoutMasterProductImages = allSelectableProductImages?.filter((item) => !checkIsMaster(item));

    updateSelectedProductsActionHandler(
      configurations,
      searchFormValues,
      selectedArea,
      isInterferometricStackSearch,
      withoutMasterProductImages,
      isChecked,
      language
    );

    if (!isChecked) {
      const allQuicklook = getAllQuicklookPreviewsFromProductImages(allQuicklookPreviews, withoutMasterProductImages)
        .map((quicklookPreview: QuicklookPreviewType) => toggleQuicklookPreviewFrom(quicklookPreview, false))
      updateQuicklookPreviewsActionHandler(allQuicklook)
    }
  }, [allQuicklookPreviews, allSelectableProductImages, configurations, formData.masterId, isInterferometricStackSearch, language, searchFormValues, selectedArea, updateQuicklookPreviewsActionHandler, updateSelectedProductsActionHandler]);

  return (
    <div className="sidebar-secondpart-nav row m-0 p-0">
      <div className="col-2 d-flex align-items-center h-100 px-2">
        <div className="container-select-all-products">
          <InputCheckbox
            name="selectAllCheckBox"
            tooltip={isSelectAllChecked ? translations("deselectAllAvailableHeaderSearch", language) : translations("selectAllAvailableHeaderSearch", language)}
            checked={isSelectAllChecked}
            disabled={allSelectableProductImages.length === 0}
            onChange={onChangeSelectAll}
          />

          <span className="label-select-all-products">{translations("selectAllHeaderSearch", language)}</span>
        </div>

      </div>

      {/* max baseline */}
      {!isInterferometricStackSearch && (
        <div className="col-3 d-flex align-items-center justify-content-end">
          <div style={{display: "flex", flexDirection: "column"}}>
            <span style={{fontSize: "smaller"}}>{translations("maxBaseline", language)}</span>
            <input
              value={maxBaselineInputValue}
              className={`form-control ${isMaxBaselineInvalid ? "is-invalid" : ""}`}
              onChange={onChangeBaselineInput}
              placeholder={translations("enterValue", language)}
            />
          </div>
        </div>
      )}

      {/* view control buttons */}
      <div className="col-3 p-0 d-flex align-items-center justify-content-start">
        {isStackEnabledOnMission(configurations, appParameters, getScopeConfigurationFieldsBy(configurations), searchFormValues) && (
          <>
            <Tooltip
              {...DEFAULT_TOOLTIP_PROPS}
              title={translations("stackHeaderSearch", language)}
              placement="top"
            >
              <button
                className={classNames("product-view-btn remove-btn-defaults", {
                  active: isInterferometricStackSearch
                })}
                type="button"
                onClick={() => {
                  search("STACK_HEADER_SEARCH");
                }}
              >
                <svg>
                  <use xlinkHref="#stack-images" fill="#ffffff" />
                </svg>
              </button>
            </Tooltip>

            <Tooltip
              {...DEFAULT_TOOLTIP_PROPS}
              title={translations("imagesHeaderSearch", language)}
              placement="top"
            >
              <button
                type="button"
                className={classNames("product-view-btn remove-btn-defaults", {
                  active: !isInterferometricStackSearch
                })}
                onClick={() => {
                  search("IMAGES_HEADER_SEARCH");
                }}
              >
                <svg>
                  <use xlinkHref="#single-image" fill="#ffffff" />
                </svg>
              </button>
            </Tooltip>
          </>
        )}
        <>
          <Tooltip
            {...DEFAULT_TOOLTIP_PROPS}
            title={translations("listHeaderSearch", language)}
            placement="top"
          >
            <button
              type="button"
              className={classNames("product-view-btn remove-btn-defaults", {
                active: isListViewActive
              })}
              onClick={() => toggleProductsViewActionHandler(true)}
            >
              <svg>
                <use xlinkHref="#product-list-icon" fill="#ffffff" />
              </svg>
            </button>
          </Tooltip>
          <Tooltip
            {...DEFAULT_TOOLTIP_PROPS}
            title={translations("matrixHeaderSearch", language)}
            placement="top"
          >
            <button
              type="button"
              className={classNames("product-view-btn remove-btn-defaults", {
                active: !isListViewActive
              })}
              onClick={() => toggleProductsViewActionHandler(false)}
            >
              <svg>
                <use xlinkHref="#product-large-icon" fill="#ffffff" />
              </svg>
            </button>
          </Tooltip>
          {
            !formData.routine && (
              <Tooltip
                {...DEFAULT_TOOLTIP_PROPS}
                title={translations("framingOnSelect", language)}
                placement="top"
              >
                <button
                  type="button"
                  className={classNames("framing-view-btn remove-btn-defaults", {
                    active: isFramingViewActive
                  })}
                  onClick={() => toggleFramingViewActionHandler(!isFramingViewActive)}
                >
                  <svg>
                    <use xlinkHref="#processsingservices-icon" fill="#ffffff" />
                  </svg>
                </button>
              </Tooltip>
            )
          }
        </>
      </div>

      <div className="col-4">
        <div className="d-flex align-items-center h-100">
          <Select
            name="orderByFieldName"
            className="order-by-field-name-selection"
            options={orderByFieldNameOptions}
            value={orderByFieldNameOptions.find((option) => option.value === formData.orderBy.fieldName)}
            onChange={(option) => {
              if (option.value !== formData.orderBy.fieldName) {
                const newFormData = {
                  ...formData,
                  token: null,
                  orderBy: {
                    ...formData.orderBy,
                    fieldName: option.value
                  },
                };

                search("ORDER_BY_FIELD_NAME", newFormData);
              }
            }}
          />
          <Select
            components={FieldTypeSelectComponents}
            name="orderByFieldType"
            className="order-by-field-type-selection"
            options={orderByFieldTypeOptions}
            value={orderByFieldTypeOptions.find((option) => option.value === formData.orderBy.sortType)}
            onChange={(option) => {
              if (option.value !== formData.orderBy.sortType) {
                const newFormData = {
                  ...formData,
                  token: null,
                  orderBy: {
                    ...formData.orderBy,
                    sortType: option.value
                  },

                };

                search("ORDER_BY_SORT_TYPE", newFormData);
              }
            }}
          />
        </div>
      </div>
    </div>
  )
};

const mapStateToProps = (state) => ({
  ordersSubmitted: state.cart.ordersSubmitted,
  isListViewActive: state.product.isListViewActive,
  isFramingViewActive: state.product.isFramingViewActive,
  configurations: state.application.configurations,
  formData: state.application.formData,
  selectedProducts: state.product.selectedProducts,
  searchFormValues: getSearchFormValuesByState(state),
  selectedArea: state.geoMap.lastShape,
  allQuicklookPreviews: state.geoMap.allQuicklookPreviews,
  language: state.application.appParameters.language,
  searchByProducts: state.geoMap.searchByProducts,
  searchProductIds: state.geoMap.searchProductIds,
  appParameters: state.application.appParameters,
});

const mapDispatchToProps = (dispatch) => ({
  toggleProductsViewActionHandler: (e) => dispatch(toggleProductsViewAction(e)),
  searchDispatchActionHandler: (formData, isNewSearch, isInterferometricStackSearch, language, searchByProducts, searchProductIds) => dispatch(searchDispatchAction(formData, isNewSearch, isInterferometricStackSearch, language, searchByProducts, searchProductIds)),
  updateSelectedProductsActionHandler: (configurations, searchFormValues, selectedArea, isInterferometricStackSearch, products, add, language) => dispatch(updateSelectedProductsDispatchAction(configurations, searchFormValues, selectedArea, isInterferometricStackSearch, products, add, language)),
  updateQuicklookPreviewsActionHandler: (quicklookPreviews) => dispatch(updateQuicklookPreviewsAction(quicklookPreviews)),
  updateFormDataPayloadActionHandler: (maxBaselineData) => dispatch(updateFormDataPayloadAction(maxBaselineData)),
  toggleFramingViewActionHandler: (e) => dispatch(toggleFramingViewAction(e)),
});

export default connect(mapStateToProps, mapDispatchToProps)(HeaderSearchResultsSidebar)
